import { differenceInCalendarDays, addDays } from 'date-fns';
import {
  addPeriod,
  keepFractionPeriod,
  halfPeriod,
  topla,
  mergeDurmalar,
  durmaGunSayisi,
  applyDurma,
} from './periodMath';
import { hesaplaGorunumAraligi } from './timeline-helpers';

/**
 * TCK 66/2 yaş indirimi — failin yaşına göre KALAN oran.
 * 12–15 → sürelerin yarısı (1/2), 15–18 → üçte ikisi (2/3), yetişkin → tamamı (1/1).
 */
const yasGrubuOranlari = {
  yetiskin: { keep: [1, 1], etiket: '18 yaş ve üzeri' },
  '15-18': { keep: [2, 3], etiket: '15–18 yaş' },
  '12-15': { keep: [1, 2], etiket: '12–15 yaş' },
};

/**
 * Kesme, süre-sonu GÜNÜNDE gerçekleşirse etkili sayılsın mı?
 * Tutucu yorum: süre o gün henüz "geçmiş" sayılmadığından kesme etkilidir (<=).
 */
const KESME_SINIR_DAHIL = true;
const within = (tarih, sinir) => (KESME_SINIR_DAHIL ? tarih <= sinir : tarih < sinir);

/**
 * Dava zamanaşımını hesaplar (TCK 66–67).
 *
 * @param {Date}   sucTarihi
 * @param {number} zamanAsimiSuresi   TCK 66/1 süresi (yıl): 8/15/20/25/30
 * @param {Array}  durduranSebepler   [{ baslangic, bitis, tip, aciklama }]  (TCK 67/1 durma)
 * @param {Array}  kesenSebepler      [{ tarih, tip, aciklama }]             (TCK 67/2 kesilme)
 * @param {string} yasGrubu           'yetiskin' | '15-18' | '12-15'
 * @param {Date}   [bugun=new Date()] testlerde deterministik kılmak için enjekte edilebilir
 */
export function hesaplaZamanAsimi(
  sucTarihi,
  zamanAsimiSuresi,
  durduranSebepler = [],
  kesenSebepler = [],
  yasGrubu = 'yetiskin',
  bugun = new Date()
) {
  const oran = yasGrubuOranlari[yasGrubu];
  if (!oran) {
    throw new Error('Geçersiz yaş grubu');
  }

  const suc = new Date(sucTarihi);

  // --- Süreler (TCK 66/1 → 66/2 → 67/4) ---
  const normalSure = { year: zamanAsimiSuresi, month: 0, day: 0, fine: 0 };
  const ayarlanmisSure = keepFractionPeriod(normalSure, oran.keep[0], oran.keep[1]);
  const kanuniSureninYarisi = halfPeriod(ayarlanmisSure);

  const mergedDurma = mergeDurmalar(durduranSebepler);

  // --- Olağanüstü (mutlak) üst sınır: TCK 67/4 ---
  // Suç + ayarlanmış süre + sürenin yarısı + (suçtan itibaren) durma süreleri.
  // Kesmelerden bağımsızdır; bu yüzden kesme döngüsünden ÖNCE hesaplanır ve
  // kesmenin etkili olup olmadığını sınırlamada da kullanılır.
  // Üst sınır süresi = uygulanan süre + yarısı, TCK 72 taşımasıyla tek süre olarak (×1,5)
  const ustSinirSuresi = topla(ayarlanmisSure, kanuniSureninYarisi);
  const ceilingBase = addPeriod(suc, ustSinirSuresi);
  const { deadline: ceiling } = applyDurma(ceilingBase, suc, mergedDurma);

  // --- Olağan zamanaşımı: KRONOLOJİK olay simülasyonu (67/1 durma + 67/2-3 kesme) ---
  // Tüm olaylar (durma başlangıçları + kesmeler) tarih sırasına dizilir; "işleyen
  // zamanaşımı tarihi" (deadline) her olaydan sonra güncellenir. Kronolojik işlem,
  // bir kesmeden sonra başlayan durmanın yeni koşuya ait olmasını doğal olarak sağlar
  // ve aynı anda adım adım hesap dökümünü (hesapAdimlari) üretir.
  const siraliKesmeler = [...kesenSebepler]
    .map((k) => ({ ...k, _t: new Date(k.tarih) }))
    .sort((a, b) => a._t - b._t);

  // Birleştirilmiş durma aralıkları (örtüşme tek sayılır) + her birinin sebep tipleri
  const durmaOlaylari = mergedDurma.map((dm) => {
    const ilgili = (durduranSebepler || []).filter(
      (o) => new Date(o.bitis) > dm.start && new Date(o.baslangic) < dm.end
    );
    const tipler = [...new Set(ilgili.map((o) => o.tip).filter(Boolean))];
    return { kind: 'durma', date: dm.start, start: dm.start, end: dm.end, gun: durmaGunSayisi(dm.start, dm.end), tipler };
  });
  const kesmeOlaylari = siraliKesmeler.map((k) => ({
    kind: 'kesme',
    date: k._t,
    tip: k.tip,
    aciklama: k.aciklama,
  }));

  // Tarihe göre; aynı günde durma (saati durdurur) kesmeden önce işlenir
  const olaylar = [...durmaOlaylari, ...kesmeOlaylari].sort((a, b) => {
    const fark = a.date - b.date;
    if (fark !== 0) return fark;
    return a.kind === 'durma' ? -1 : 1;
  });

  const runs = []; // önce üzeri çizilen koşular; aktif koşu döngü sonunda eklenir
  const kesmelerModel = [];
  const hesapAdimlari = [];
  let adimNo = 1;

  let runStart = new Date(suc);
  let deadline = addPeriod(runStart, ayarlanmisSure);
  let runDurmaGun = 0;

  hesapAdimlari.push({
    no: adimNo++,
    tarih: new Date(suc),
    kind: 'baslangic',
    tckRef: 'TCK 66/6',
    baslik: 'Zamanaşımı işlemeye başladı',
    etki: 'Uygulanan süre, suç tarihinden itibaren işlemeye başladı',
    deltaGun: null,
    bitis: new Date(deadline),
    donem: 1,
  });

  for (const ev of olaylar) {
    if (ev.kind === 'durma') {
      const canli = ev.start >= runStart && ev.start < deadline && ev.start <= ceiling;
      if (canli && ev.gun > 0) {
        deadline = addDays(deadline, ev.gun);
        runDurmaGun += ev.gun;
        hesapAdimlari.push({
          no: adimNo++,
          tarih: new Date(ev.start),
          kind: 'durma',
          tckRef: 'TCK 67/1',
          baslik: ev.tipler.length ? ev.tipler.join(', ') : 'Durma',
          etki: 'Zamanaşımı süresi durdu',
          araStart: new Date(ev.start),
          araEnd: new Date(ev.end),
          deltaGun: ev.gun,
          bitis: new Date(deadline),
          donem: runs.length + 1,
        });
      } else {
        hesapAdimlari.push({
          no: adimNo++,
          tarih: new Date(ev.start),
          kind: 'durma-pasif',
          tckRef: 'TCK 67/1',
          baslik: ev.tipler.length ? ev.tipler.join(', ') : 'Durma',
          etki: 'Süre işlemediğinden etkisiz (kapsam dışı)',
          araStart: new Date(ev.start),
          araEnd: new Date(ev.end),
          deltaGun: null,
          bitis: null,
          donem: runs.length + 1,
        });
      }
    } else {
      const kesmeTarihi = ev.date;
      const canli =
        kesmeTarihi >= runStart && within(kesmeTarihi, deadline) && within(kesmeTarihi, ceiling);
      if (canli) {
        runs.push({
          start: new Date(runStart),
          end: new Date(deadline),
          supersededAt: new Date(kesmeTarihi),
          isActive: false,
          cappedAtCeiling: false,
          durmaGun: runDurmaGun,
          reason: runs.length === 0 ? 'baslangic' : 'kesme',
        });
        const kesmeNo = runs.length;
        kesmelerModel.push({
          date: new Date(kesmeTarihi),
          type: ev.tip || 'Kesme',
          label: ev.aciklama || ev.tip || 'Kesme',
          effective: true,
          runIndex: runs.length,
        });
        runStart = new Date(kesmeTarihi);
        deadline = addPeriod(runStart, ayarlanmisSure);
        runDurmaGun = 0;
        hesapAdimlari.push({
          no: adimNo++,
          tarih: new Date(kesmeTarihi),
          kind: 'kesme',
          tckRef: 'TCK 67/2',
          baslik: `${kesmeNo}. kesme${ev.tip ? ' — ' + ev.tip : ''}`,
          etki: 'Süre kesildi; bu tarihten itibaren yeniden işlemeye başladı',
          deltaGun: null,
          bitis: new Date(deadline),
          donem: runs.length + 1,
        });
      } else {
        kesmelerModel.push({
          date: new Date(kesmeTarihi),
          type: ev.tip || 'Kesme',
          label: ev.aciklama || ev.tip || 'Kesme',
          effective: false,
          runIndex: -1,
        });
        hesapAdimlari.push({
          no: adimNo++,
          tarih: new Date(kesmeTarihi),
          kind: 'kesme-etkisiz',
          tckRef: 'TCK 67/2',
          baslik: `Kesme (etkisiz)${ev.tip ? ' — ' + ev.tip : ''}`,
          etki: 'Süre dolduğundan/üst sınır aşıldığından yeniden başlatmaz',
          deltaGun: null,
          bitis: null,
          donem: runs.length + 1,
        });
      }
    }
  }

  const effectiveKesmeCount = runs.length;
  const hasKesme = effectiveKesmeCount > 0;
  const ordinaryDeadline = new Date(deadline);
  const cappedAtCeiling = hasKesme && ordinaryDeadline > ceiling;

  // Aktif (son) koşu
  runs.push({
    start: new Date(runStart),
    end: new Date(ordinaryDeadline),
    supersededAt: null,
    isActive: true,
    cappedAtCeiling,
    durmaGun: runDurmaGun,
    reason: effectiveKesmeCount === 0 ? 'baslangic' : 'kesme',
  });
  runs.forEach((r, i) => {
    r.index = i;
  });

  // --- Etkin (operatif) baraj tarihi ---
  // Kesme yoksa olağan tarihte düşer; 1.5× tavan yalnızca kesme varsa devreye girer.
  const effective = hasKesme
    ? ordinaryDeadline > ceiling
      ? new Date(ceiling)
      : new Date(ordinaryDeadline)
    : new Date(ordinaryDeadline);

  const zamanAsimiDoldu = bugun > effective;
  const kalanGun = differenceInCalendarDays(effective, bugun); // negatif → dolmuş

  // Operatif [suç, etkin tarih) aralığında fiilen süreyi öteleyen toplam durma süresi.
  // (Suçtan önceki, ya da etkin tarihi etkilemeyen — ör. kesme yokken üst sınıra düşen —
  // durmalar özet sayısına dahil edilmez.)
  const toplamDurmaGun = mergedDurma
    .filter((dm) => dm.start >= suc && dm.start < effective)
    .reduce((acc, dm) => acc + durmaGunSayisi(dm.start, dm.end), 0);

  // --- Görselleştirme modeli ---
  const farEnd = new Date(Math.max(ceiling.getTime(), ordinaryDeadline.getTime()));
  const { bas: rangeStart, bit: rangeEnd } = hesaplaGorunumAraligi(suc, farEnd, bugun);

  const findRunIndex = (tarih) => {
    if (tarih < suc) return -1;
    let idx = -1;
    runs.forEach((r, i) => {
      if (r.start <= tarih) idx = i;
    });
    return idx;
  };

  const durmalarModel = (durduranSebepler || []).map((d) => {
    const s = new Date(d.baslangic);
    const e = new Date(d.bitis);
    return {
      start: s,
      end: e,
      type: d.tip || 'Durma',
      label: d.aciklama || d.tip || 'Durma',
      gun: durmaGunSayisi(s, e),
      runIndex: findRunIndex(s),
    };
  });

  const markers = [{ date: new Date(suc), kind: 'suc', label: 'Suç tarihi' }];
  if (hasKesme) {
    markers.push({ date: new Date(runs[0].end), kind: 'ilkZA', label: 'İlk Z.A.' });
  }
  markers.push({ date: new Date(ordinaryDeadline), kind: 'olaganZA', label: 'Olağan Z.A.' });
  markers.push({ date: new Date(ceiling), kind: 'olaganustuZA', label: 'Olağanüstü Z.A.' });
  if (bugun >= rangeStart && bugun <= rangeEnd) {
    markers.push({ date: new Date(bugun), kind: 'bugun', label: 'Bugün' });
  }

  const timelineModel = {
    rangeStart,
    rangeEnd,
    sucTarihi: new Date(suc),
    bugun: new Date(bugun),
    runs,
    ceiling: { start: new Date(suc), end: new Date(ceiling), active: hasKesme },
    durmalar: durmalarModel,
    kesmeler: kesmelerModel,
    markers,
    ordinaryDeadline: new Date(ordinaryDeadline),
    effective: new Date(effective),
    capApplied: cappedAtCeiling,
    zamanAsimiDoldu,
    kalanGun,
    toplamDurmaGun,
  };

  return {
    // --- Operatif sonuç ---
    olaganZamanAsimi: new Date(ordinaryDeadline),
    olaganustuZamanAsimi: new Date(ceiling),
    effective: new Date(effective),
    capApplied: cappedAtCeiling,
    tck674CapUygulandi: cappedAtCeiling, // geriye dönük ad
    kanuniSureninYarisi,
    toplamDurmaSuresi: toplamDurmaGun, // (eski tüketicilerle sözleşme — artık doğru ad)
    kalanGun,
    sonKesilmeTarihi: hasKesme ? new Date(runs[runs.length - 1].start) : null,
    zamanAsimiDoldu,
    olaganZamanAsimiDoldu: bugun > ordinaryDeadline,
    olaganustuZamanAsimiDoldu: bugun > ceiling,
    hasKesme,
    etkiliKesmeSayisi: effectiveKesmeCount,
    bugun: new Date(bugun),
    hesapAdimlari,
    yasGrubuBilgisi: {
      grup: yasGrubu,
      etiket: oran.etiket,
      normalSure,
      ayarlanmisSure,
      olaganSure: ayarlanmisSure,
      kanuniSureninYarisi,
    },
    timelineModel,
  };
}
